import { NgClass, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { signal, Injectable, input, output, computed, linkedSignal, ChangeDetectionStrategy, Component, EventEmitter, Input, Output, Inject } from '@angular/core';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import * as i3 from '@angular/material/input';
import { MatInputModule } from '@angular/material/input';
import * as i1 from '@polaris-customerweb/sharedcomponents.shared';
import { GeoBounds, Condition, SaveCancelDeleteBarComponent, GeoJsonFeatureCollection, GeoCoordinate, MailingAddress, DayHours } from '@polaris-customerweb/sharedcomponents.shared';
import { switchMap, of, combineLatest, filter, take, shareReplay, skip, startWith, BehaviorSubject, map, fromEvent, timer, takeWhile } from 'rxjs';
import { noop, defer, isEmpty, startsWith, isArray, cloneDeep, get, intersection, merge } from 'lodash-es';
import * as i4 from '@angular/cdk/text-field';
import * as i1$1 from '@polaris-customerweb/sharedcomponents.config';
import mapboxgl from 'mapbox-gl';
import { WINDOW } from '@polaris-customerweb/sharedcomponents.core';

class PrimaryMapService {
    constructor() {
        this.primaryMapSignal = signal(undefined);
        this.primaryMapExtentsSignal = signal(undefined);
        this.onMoveEnd = noop;
        this.$primaryMap = this.primaryMapSignal.asReadonly();
        this.$primaryMapExtents = this.primaryMapExtentsSignal.asReadonly();
    }
    setPrimaryMap(map) {
        this.updateExtents(map);
        this.primaryMapSignal.set(map);
    }
    updateExtents(newMap) {
        const currentMap = this.$primaryMap();
        if (currentMap) {
            currentMap.off('moveend', this.onMoveEnd);
        }
        if (newMap) {
            this.onMoveEnd = () => {
                const center = newMap.getCenter();
                this.primaryMapExtentsSignal.set({
                    bounds: GeoBounds.fromMapboxLngLatBounds(newMap.getBounds()),
                    center: [center.lng, center.lat],
                    zoom: newMap.getZoom(),
                });
            };
            newMap.on('moveend', this.onMoveEnd);
        }
        else {
            this.primaryMapExtentsSignal.set(undefined);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: PrimaryMapService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: PrimaryMapService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: PrimaryMapService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [] });

const OffroadConditionTypes = [
    'Closed',
    'Dry',
    'Dusty',
    'FallenTree',
    'Flooded',
    'Good',
    'Hazard',
    'Mud',
    'OK',
    'Poor',
    'Rocks',
    'Washout',
];
const SnoConditionTypes = [
    'Closed',
    'FallenTree',
    'Good',
    'Hazard',
    'Hole',
    'OK',
    'OpenWater',
    'Poor',
    'Rocks',
];
class ConditionCreateEditComponent {
    constructor(conditionService, primaryMapService, rideTypeService, uuidService) {
        this.conditionService = conditionService;
        this.primaryMapService = primaryMapService;
        this.rideTypeService = rideTypeService;
        // inputs
        this.conditionId = input('');
        this.coordinate = input();
        // outputs
        this.closeClicked = output();
        this.saveClicked = output();
        this.deleteClicked = output();
        this.$isSno = rideTypeService.$isSnoRideType;
        this.$conditionTypes = computed(() => {
            const isSno = this.$isSno();
            const types = isSno ? [...SnoConditionTypes] : [...OffroadConditionTypes];
            return types.map((type) => ({
                icon: Condition.getIconForConditionType(type, isSno ? 'SNO' : 'ORV'),
                name: Condition.getConditionNameFromType(type),
                type,
            }));
        });
        const condition = toSignal(toObservable(this.conditionId).pipe(switchMap((id) => (uuidService.isEmpty(id) ? of(new Condition()) : conditionService.getConditionById(id)))), { initialValue: new Condition() });
        this.$condition = linkedSignal(() => condition());
        this.$isEdit = computed(() => Boolean(this.$condition().id));
    }
    typeChange(type) {
        this.$condition.update((condition) => {
            return new Condition({
                ...condition.toContract(),
                type,
            });
        });
    }
    descriptionChange(event) {
        this.$condition.update((condition) => {
            return new Condition({
                ...condition.toContract(),
                description: event.target.value,
            });
        });
    }
    onSave() {
        const condition = this.$condition();
        condition.coordinate = this.coordinate() ?? this.primaryMapService.$primaryMapExtents()?.center ?? [0, 0];
        condition.rideType = this.rideTypeService.$isSnoRideType() ? 'SNO' : 'ORV';
        condition.reportedDate = new Date();
        (condition.id
            ? this.conditionService.patchCondition(condition)
            : this.conditionService.addCondition(condition)).subscribe((res) => {
            this.saveClicked.emit(res.id);
        });
    }
    onClose() {
        this.closeClicked.emit();
    }
    onDelete() {
        const id = this.$condition().id;
        this.conditionService.deleteCondition(id).subscribe(() => {
            this.deleteClicked.emit(id);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ConditionCreateEditComponent, deps: [{ token: i1.ConditionService }, { token: PrimaryMapService }, { token: i1.RideTypeService }, { token: i1.UuidService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.14", type: ConditionCreateEditComponent, isStandalone: true, selector: "pii-condition-create-edit", inputs: { conditionId: { classPropertyName: "conditionId", publicName: "conditionId", isSignal: true, isRequired: false, transformFunction: null }, coordinate: { classPropertyName: "coordinate", publicName: "coordinate", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { closeClicked: "closeClicked", saveClicked: "saveClicked", deleteClicked: "deleteClicked" }, ngImport: i0, template: "<div class=\"condition-create-edit-container detail-container\">\n  <div class=\"header-container\">\n    <div>\n      @if ($isEdit()) {\n        <h2 i18n>Edit Condition</h2>\n      } @else {\n        <h2 i18n>Add Condition</h2>\n      }\n    </div>\n    <div class=\"helper-align-right\">\n      <a\n        id=\"closeConditionCreateEdit\"\n        href=\"javascript:void(0);\"\n        aria-label=\"close\"\n        (click)=\"onClose()\"\n        i18n-aria-label\n      >\n        <mat-icon svgIcon=\"pii:close\" class=\"close-icon black\" />\n      </a>\n    </div>\n    <div i18n>Move the pin to nail the exact spot.</div>\n  </div>\n  <div class=\"content-container\">\n    <mat-form-field>\n      <textarea\n        matInput\n        cdkTextareaAutosize\n        placeholder=\"details\"\n        [value]=\"$condition().description\"\n        (change)=\"descriptionChange($event)\"\n        i18n-placeholder\n      ></textarea>\n    </mat-form-field>\n    <div class=\"type-container\" [ngClass]=\"{ sno: $isSno() }\">\n      @for (type of $conditionTypes(); track $index) {\n        <button\n          class=\"type\"\n          type=\"button\"\n          [ngClass]=\"{ selected: type.type === $condition().type }\"\n          (click)=\"typeChange(type.type)\"\n        >\n          <mat-icon [svgIcon]=\"type.icon\" />\n          <div>{{ type.name }}</div>\n        </button>\n      }\n    </div>\n  </div>\n  <div class=\"detail-footer\">\n    <pii-save-cancel-delete-bar\n      saveButtonId=\"saveMaintenanceRecord\"\n      [cancelButtonId]=\"`cancelCondition${$isEdit() ? 'Edit' : 'Create'}`\"\n      [showDelete]=\"$isEdit()\"\n      (doCancel)=\"onClose()\"\n      (doDelete)=\"onDelete()\"\n      (doSave)=\"onSave()\"\n    />\n  </div>\n</div>\n", styles: [".detail-container{display:flex;flex-direction:column;height:100%;min-height:100%;overflow:hidden}.detail-container .header-container{align-items:center;background:#fff;border-bottom:1px solid #d8d8d8;display:grid;flex:0 0 auto;grid-template-columns:1fr auto;padding:16px}.detail-container .header-container h2{font-size:1.2rem;line-height:1.2rem;margin:0}.detail-container .content-container{background:#f5f6f7f0;display:flex;flex:1 1 auto;flex-direction:column;overflow:auto}.detail-container .footer-container{background:#fff;flex:0 0 auto}.helper-bold{font-weight:700}.helper-full-width{width:100%}.helper-warning{color:red}.material-switch-input{display:none}.material-switch-label{cursor:pointer;display:inline-block;min-width:42px;position:relative;text-align:start}.material-switch-label:before,.material-switch-label:after{content:\"\";margin:0;outline:0;position:absolute;top:50%;transform:translateY(-50%);transition:all .3s ease}.material-switch-label:before{height:14px;width:34px;background-color:gray;border-radius:8px;left:1px}.material-switch-label:after{height:26px;width:26px;background-color:#d8d8d8;border-radius:50%;box-shadow:0 3px 1px -2px #00000024,0 2px 2px #00000019,0 1px 5px #00000015;left:0}.material-switch-label .toggle-on{display:none}.material-switch-label .toggle-off{display:inline-block}.material-switch-input:checked+.material-switch-label:before{background-color:var(--primary-color-lighter)}.material-switch-input:checked+.material-switch-label:after{background-color:var(--primary-color);transform:translate(80%,-50%)}.material-switch-input:checked+.material-switch-label .toggle-on{display:inline-block}.material-switch-input:checked+.material-switch-label .toggle-off{display:none}mat-expansion-panel-header{box-shadow:0 1px 3px 0 var(--shadow-color);height:54px}mat-expansion-panel-header.mat-expanded{height:54px}.mat-mdc-checkbox label{margin-bottom:0}.mat-mdc-checkbox input[disabled]{opacity:0}.mat-mdc-checkbox.multiline-checkbox .mdc-form-field{align-items:start}.mat-mdc-checkbox .mdc-checkbox .mdc-checkbox__native-control:enabled:not(:checked):not(:indeterminate):not([data-indeterminate=true])~.mdc-checkbox__background{border-color:#000!important}.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:not(:checked):not(:indeterminate):not([data-indeterminate=true])~.mdc-checkbox__background,.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:checked~.mdc-checkbox__background,.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:indeterminate~.mdc-checkbox__background,.mat-mdc-checkbox .mdc-checkbox__native-control[data-indeterminate=true][disabled]~.mdc-checkbox__background{background-color:transparent!important;border-color:#d8d8d8!important}.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:not(:checked):not(:indeterminate):not([data-indeterminate=true])~.mdc-checkbox__background svg,.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:checked~.mdc-checkbox__background svg,.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:indeterminate~.mdc-checkbox__background svg,.mat-mdc-checkbox .mdc-checkbox__native-control[data-indeterminate=true][disabled]~.mdc-checkbox__background svg{color:#d8d8d8!important}.mat-mdc-checkbox.mat-mdc-checkbox-disabled label{color:gray!important}.remove-bottom-ripple .mdc-text-field--filled .mdc-line-ripple:after{border-bottom:none}.remove-bottom-ripple.input-has-value .mat-mdc-form-field-infix{min-height:40px!important;padding:8px!important}.expanding-height-textarea .mdc-text-field--filled:not(.mdc-text-field--disabled){border:0!important;border-radius:0!important}.expanding-height-textarea .mat-mdc-form-field-subscript-wrapper{display:none}.mdc-text-field--filled:not(.mdc-text-field--disabled) .mdc-line-ripple:before{border-bottom-color:transparent!important}.mat-mdc-form-field-focus-overlay{background-color:#fff!important}.mat-mdc-form-field-infix{min-height:50px!important;padding-top:17px!important}.mdc-text-field--filled:not(.mdc-text-field--disabled){background-color:#fff!important;border:1px solid #d8d8d8;border-radius:5px}.mdc-text-field--filled:not(.mdc-text-field--disabled).mdc-text-field--invalid{border-color:#d0021b}.mdc-floating-label{color:gray!important}.mdc-text-field--invalid.mdc-text-field:not(.mdc-text-field--disabled) .mdc-text-field__input{color:#d0021b!important}.mat-mdc-form-field-subscript-wrapper{bottom:18px;height:20px!important;left:-5px;position:relative}.hide-input-error .mat-mdc-form-field-subscript-wrapper{height:0!important}.mat-mdc-form-field-hint-wrapper,.mat-mdc-form-field-error-wrapper{left:-5px!important;top:15px!important}.mat-mdc-floating-label{top:25px!important}.mat-mdc-form-field input[type=color],.mat-mdc-form-field input[type=date],.mat-mdc-form-field input[type=datetime-local],.mat-mdc-form-field input[type=datetime],.mat-mdc-form-field input[type=email],.mat-mdc-form-field input[type=month],.mat-mdc-form-field input[type=number],.mat-mdc-form-field input[type=password],.mat-mdc-form-field input[type=search],.mat-mdc-form-field input[type=tel],.mat-mdc-form-field input[type=text],.mat-mdc-form-field input[type=time],.mat-mdc-form-field input[type=url],.mat-mdc-form-field input[type=week]{border:initial!important;height:initial!important;padding:initial!important}.mat-mdc-form-field.inherit{display:inherit!important}.responsive-img{display:block;height:100%;max-width:100%;width:100%}.responsive-img-cover{height:100%;object-fit:cover;width:100%}.responsive-img-contain{height:auto;object-fit:contain;width:100%}.display-flex-container{display:flex}.flex-1-auto{flex:1 1 auto}.flex-1{flex:1}.flex-2{flex:2}.flex-3{flex:3}.list-unstyled,.list-inline{list-style:none;margin:0;padding:0}.list-inline>li{display:inline-block;padding:0}.default-color,.profile-blue{background:#09c}.profile-darkblue,.profile-dark-blue{background:#05f}.profile-brown{background:#7f4f04}.profile-gold{background:#fc0}.profile-green{background:#26c407}.profile-darkgreen{background:#090}.profile-orange{background:#ff7e00}.profile-purple{background:#90c}.profile-red{background:#c00}.profile-salmon{background:#f66}.primary-bg{background:var(--primary-color)}*{box-sizing:border-box}body{font-size:1rem;line-height:1.4rem}a{color:var(--primary-color);text-decoration:none}h1{font-size:2.25rem;line-height:2.25rem;font-family:var(--brand-font);margin:0}h1.small-h1{font-size:1.1rem;line-height:1.1rem;font-family:Helvetica,serif}.story-wrapper{background-color:#ffffffd9;border-radius:10px;box-shadow:0 2px 8px #57192133;font-family:Helvetica,serif;padding:24px;width:max-content}.story-wrapper.narrow{max-width:300px}.story-wrapper.zero-padding{padding:0}.story-wrapper .small-padding{padding:0 4px}.story-wrapper .medium-padding{padding:0 8px}.story-wrapper .large-padding{padding:0 16px}.story-wrapper .x-large-padding{padding:0 24px}.story-wrapper .xx-large-padding{padding:0 32px}.type-container{box-sizing:border-box;display:grid;gap:4px;grid-template-columns:repeat(4,1fr);padding:4px;width:100%}.type-container.sno{grid-template-columns:repeat(3,1fr)}.type-container .type{align-items:center;border:1px solid #d8d8d8;border-radius:5px;cursor:pointer;display:flex;flex-direction:column;gap:5px;justify-content:center;padding:0;width:100%}.type-container .type mat-icon{height:40%;width:40%}.type-container .type.selected{background-color:#d8d8d8;border-color:var(--primary-color)}.detail-footer{padding:8px}\n"], dependencies: [{ kind: "component", type: MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }, { kind: "ngmodule", type: MatInputModule }, { kind: "directive", type: i3.MatInput, selector: "input[matInput], textarea[matInput], select[matNativeControl],      input[matNativeControl], textarea[matNativeControl]", inputs: ["disabled", "id", "placeholder", "name", "required", "type", "errorStateMatcher", "aria-describedby", "value", "readonly", "disabledInteractive"], exportAs: ["matInput"] }, { kind: "component", type: i3.MatFormField, selector: "mat-form-field", inputs: ["hideRequiredMarker", "color", "floatLabel", "appearance", "subscriptSizing", "hintLabel"], exportAs: ["matFormField"] }, { kind: "directive", type: i4.CdkTextareaAutosize, selector: "textarea[cdkTextareaAutosize]", inputs: ["cdkAutosizeMinRows", "cdkAutosizeMaxRows", "cdkTextareaAutosize", "placeholder"], exportAs: ["cdkTextareaAutosize"] }, { kind: "component", type: SaveCancelDeleteBarComponent, selector: "pii-save-cancel-delete-bar", inputs: ["showDelete", "deleteEnabled", "saveEnabled", "isSubmitting", "cancelButtonId", "cancelAnalytics", "deleteButtonId", "deleteAnalytics", "saveButtonId", "saveAnalytics", "saveAnalyticsData", "saveButtonText", "savingButtonText"], outputs: ["doCancel", "doDelete", "doSave"] }, { kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ConditionCreateEditComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, imports: [MatIcon, MatInputModule, SaveCancelDeleteBarComponent, NgClass], selector: 'pii-condition-create-edit', template: "<div class=\"condition-create-edit-container detail-container\">\n  <div class=\"header-container\">\n    <div>\n      @if ($isEdit()) {\n        <h2 i18n>Edit Condition</h2>\n      } @else {\n        <h2 i18n>Add Condition</h2>\n      }\n    </div>\n    <div class=\"helper-align-right\">\n      <a\n        id=\"closeConditionCreateEdit\"\n        href=\"javascript:void(0);\"\n        aria-label=\"close\"\n        (click)=\"onClose()\"\n        i18n-aria-label\n      >\n        <mat-icon svgIcon=\"pii:close\" class=\"close-icon black\" />\n      </a>\n    </div>\n    <div i18n>Move the pin to nail the exact spot.</div>\n  </div>\n  <div class=\"content-container\">\n    <mat-form-field>\n      <textarea\n        matInput\n        cdkTextareaAutosize\n        placeholder=\"details\"\n        [value]=\"$condition().description\"\n        (change)=\"descriptionChange($event)\"\n        i18n-placeholder\n      ></textarea>\n    </mat-form-field>\n    <div class=\"type-container\" [ngClass]=\"{ sno: $isSno() }\">\n      @for (type of $conditionTypes(); track $index) {\n        <button\n          class=\"type\"\n          type=\"button\"\n          [ngClass]=\"{ selected: type.type === $condition().type }\"\n          (click)=\"typeChange(type.type)\"\n        >\n          <mat-icon [svgIcon]=\"type.icon\" />\n          <div>{{ type.name }}</div>\n        </button>\n      }\n    </div>\n  </div>\n  <div class=\"detail-footer\">\n    <pii-save-cancel-delete-bar\n      saveButtonId=\"saveMaintenanceRecord\"\n      [cancelButtonId]=\"`cancelCondition${$isEdit() ? 'Edit' : 'Create'}`\"\n      [showDelete]=\"$isEdit()\"\n      (doCancel)=\"onClose()\"\n      (doDelete)=\"onDelete()\"\n      (doSave)=\"onSave()\"\n    />\n  </div>\n</div>\n", styles: [".detail-container{display:flex;flex-direction:column;height:100%;min-height:100%;overflow:hidden}.detail-container .header-container{align-items:center;background:#fff;border-bottom:1px solid #d8d8d8;display:grid;flex:0 0 auto;grid-template-columns:1fr auto;padding:16px}.detail-container .header-container h2{font-size:1.2rem;line-height:1.2rem;margin:0}.detail-container .content-container{background:#f5f6f7f0;display:flex;flex:1 1 auto;flex-direction:column;overflow:auto}.detail-container .footer-container{background:#fff;flex:0 0 auto}.helper-bold{font-weight:700}.helper-full-width{width:100%}.helper-warning{color:red}.material-switch-input{display:none}.material-switch-label{cursor:pointer;display:inline-block;min-width:42px;position:relative;text-align:start}.material-switch-label:before,.material-switch-label:after{content:\"\";margin:0;outline:0;position:absolute;top:50%;transform:translateY(-50%);transition:all .3s ease}.material-switch-label:before{height:14px;width:34px;background-color:gray;border-radius:8px;left:1px}.material-switch-label:after{height:26px;width:26px;background-color:#d8d8d8;border-radius:50%;box-shadow:0 3px 1px -2px #00000024,0 2px 2px #00000019,0 1px 5px #00000015;left:0}.material-switch-label .toggle-on{display:none}.material-switch-label .toggle-off{display:inline-block}.material-switch-input:checked+.material-switch-label:before{background-color:var(--primary-color-lighter)}.material-switch-input:checked+.material-switch-label:after{background-color:var(--primary-color);transform:translate(80%,-50%)}.material-switch-input:checked+.material-switch-label .toggle-on{display:inline-block}.material-switch-input:checked+.material-switch-label .toggle-off{display:none}mat-expansion-panel-header{box-shadow:0 1px 3px 0 var(--shadow-color);height:54px}mat-expansion-panel-header.mat-expanded{height:54px}.mat-mdc-checkbox label{margin-bottom:0}.mat-mdc-checkbox input[disabled]{opacity:0}.mat-mdc-checkbox.multiline-checkbox .mdc-form-field{align-items:start}.mat-mdc-checkbox .mdc-checkbox .mdc-checkbox__native-control:enabled:not(:checked):not(:indeterminate):not([data-indeterminate=true])~.mdc-checkbox__background{border-color:#000!important}.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:not(:checked):not(:indeterminate):not([data-indeterminate=true])~.mdc-checkbox__background,.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:checked~.mdc-checkbox__background,.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:indeterminate~.mdc-checkbox__background,.mat-mdc-checkbox .mdc-checkbox__native-control[data-indeterminate=true][disabled]~.mdc-checkbox__background{background-color:transparent!important;border-color:#d8d8d8!important}.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:not(:checked):not(:indeterminate):not([data-indeterminate=true])~.mdc-checkbox__background svg,.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:checked~.mdc-checkbox__background svg,.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:indeterminate~.mdc-checkbox__background svg,.mat-mdc-checkbox .mdc-checkbox__native-control[data-indeterminate=true][disabled]~.mdc-checkbox__background svg{color:#d8d8d8!important}.mat-mdc-checkbox.mat-mdc-checkbox-disabled label{color:gray!important}.remove-bottom-ripple .mdc-text-field--filled .mdc-line-ripple:after{border-bottom:none}.remove-bottom-ripple.input-has-value .mat-mdc-form-field-infix{min-height:40px!important;padding:8px!important}.expanding-height-textarea .mdc-text-field--filled:not(.mdc-text-field--disabled){border:0!important;border-radius:0!important}.expanding-height-textarea .mat-mdc-form-field-subscript-wrapper{display:none}.mdc-text-field--filled:not(.mdc-text-field--disabled) .mdc-line-ripple:before{border-bottom-color:transparent!important}.mat-mdc-form-field-focus-overlay{background-color:#fff!important}.mat-mdc-form-field-infix{min-height:50px!important;padding-top:17px!important}.mdc-text-field--filled:not(.mdc-text-field--disabled){background-color:#fff!important;border:1px solid #d8d8d8;border-radius:5px}.mdc-text-field--filled:not(.mdc-text-field--disabled).mdc-text-field--invalid{border-color:#d0021b}.mdc-floating-label{color:gray!important}.mdc-text-field--invalid.mdc-text-field:not(.mdc-text-field--disabled) .mdc-text-field__input{color:#d0021b!important}.mat-mdc-form-field-subscript-wrapper{bottom:18px;height:20px!important;left:-5px;position:relative}.hide-input-error .mat-mdc-form-field-subscript-wrapper{height:0!important}.mat-mdc-form-field-hint-wrapper,.mat-mdc-form-field-error-wrapper{left:-5px!important;top:15px!important}.mat-mdc-floating-label{top:25px!important}.mat-mdc-form-field input[type=color],.mat-mdc-form-field input[type=date],.mat-mdc-form-field input[type=datetime-local],.mat-mdc-form-field input[type=datetime],.mat-mdc-form-field input[type=email],.mat-mdc-form-field input[type=month],.mat-mdc-form-field input[type=number],.mat-mdc-form-field input[type=password],.mat-mdc-form-field input[type=search],.mat-mdc-form-field input[type=tel],.mat-mdc-form-field input[type=text],.mat-mdc-form-field input[type=time],.mat-mdc-form-field input[type=url],.mat-mdc-form-field input[type=week]{border:initial!important;height:initial!important;padding:initial!important}.mat-mdc-form-field.inherit{display:inherit!important}.responsive-img{display:block;height:100%;max-width:100%;width:100%}.responsive-img-cover{height:100%;object-fit:cover;width:100%}.responsive-img-contain{height:auto;object-fit:contain;width:100%}.display-flex-container{display:flex}.flex-1-auto{flex:1 1 auto}.flex-1{flex:1}.flex-2{flex:2}.flex-3{flex:3}.list-unstyled,.list-inline{list-style:none;margin:0;padding:0}.list-inline>li{display:inline-block;padding:0}.default-color,.profile-blue{background:#09c}.profile-darkblue,.profile-dark-blue{background:#05f}.profile-brown{background:#7f4f04}.profile-gold{background:#fc0}.profile-green{background:#26c407}.profile-darkgreen{background:#090}.profile-orange{background:#ff7e00}.profile-purple{background:#90c}.profile-red{background:#c00}.profile-salmon{background:#f66}.primary-bg{background:var(--primary-color)}*{box-sizing:border-box}body{font-size:1rem;line-height:1.4rem}a{color:var(--primary-color);text-decoration:none}h1{font-size:2.25rem;line-height:2.25rem;font-family:var(--brand-font);margin:0}h1.small-h1{font-size:1.1rem;line-height:1.1rem;font-family:Helvetica,serif}.story-wrapper{background-color:#ffffffd9;border-radius:10px;box-shadow:0 2px 8px #57192133;font-family:Helvetica,serif;padding:24px;width:max-content}.story-wrapper.narrow{max-width:300px}.story-wrapper.zero-padding{padding:0}.story-wrapper .small-padding{padding:0 4px}.story-wrapper .medium-padding{padding:0 8px}.story-wrapper .large-padding{padding:0 16px}.story-wrapper .x-large-padding{padding:0 24px}.story-wrapper .xx-large-padding{padding:0 32px}.type-container{box-sizing:border-box;display:grid;gap:4px;grid-template-columns:repeat(4,1fr);padding:4px;width:100%}.type-container.sno{grid-template-columns:repeat(3,1fr)}.type-container .type{align-items:center;border:1px solid #d8d8d8;border-radius:5px;cursor:pointer;display:flex;flex-direction:column;gap:5px;justify-content:center;padding:0;width:100%}.type-container .type mat-icon{height:40%;width:40%}.type-container .type.selected{background-color:#d8d8d8;border-color:var(--primary-color)}.detail-footer{padding:8px}\n"] }]
        }], ctorParameters: () => [{ type: i1.ConditionService }, { type: PrimaryMapService }, { type: i1.RideTypeService }, { type: i1.UuidService }] });

class MapboxService {
    constructor(config) {
        this.mapbox = mapboxgl;
        mapboxgl.accessToken = config.mapboxToken;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: MapboxService, deps: [{ token: i1$1.ConfigService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: MapboxService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: MapboxService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1$1.ConfigService }] });

class MapViewModelService {
    constructor(config, mapboxService) {
        this.config = config;
        this.mapboxService = mapboxService;
    }
    create(containerId, isSat, isSno, enableDragPan) {
        const mapInitialized$ = combineLatest([isSat, isSno])
            .pipe(filter(([isSatDefined]) => isSatDefined !== undefined))
            .pipe(take(1), switchMap(([isSatellite, isSnow]) => {
            const mapboxMap = this.createMap(containerId, isSatellite, isSnow, enableDragPan);
            return this.waitForStyleLoad(mapboxMap);
        }), shareReplay());
        return combineLatest({
            map: mapInitialized$,
            styleUpdated: mapInitialized$.pipe(switchMap((mapboxMap) => combineLatest([isSat, isSno]).pipe(skip(1), switchMap(([isSatellite, isSnow]) => {
                mapboxMap.setStyle(this.getMapBaseUri(isSatellite, isSnow));
                return this.waitForStyleLoad(mapboxMap);
            }), startWith(mapboxMap)))),
        });
    }
    createMap(containerId, isSat, isSno, enableDragPan) {
        const mapboxMap = new this.mapboxService.mapbox.Map({
            attributionControl: false,
            center: [-79.3, 56],
            container: containerId,
            style: this.getMapBaseUri(isSat, isSno),
            zoom: 7,
        });
        mapboxMap.dragRotate.disable();
        mapboxMap.touchZoomRotate.disableRotation();
        if (!enableDragPan) {
            mapboxMap.dragPan.disable();
            mapboxMap.boxZoom.disable();
            mapboxMap.doubleClickZoom.disable();
            mapboxMap.scrollZoom.disable();
            mapboxMap.keyboard.disable();
        }
        return mapboxMap;
    }
    getMapBaseUri(isSat, isSno) {
        const mapboxStandardStyleEnabled = this.config.featureFlags.areMapboxStandardStyleEnabled;
        const tileSource = isSno
            ? isSat
                ? ((mapboxStandardStyleEnabled
                    ? this.config.tileSourceV2.satelliteSnow
                    : this.config.tileSource.satelliteSnow) ??
                    (mapboxStandardStyleEnabled ? this.config.tileSourceV2.satellite : this.config.tileSource.satellite))
                : ((mapboxStandardStyleEnabled ? this.config.tileSourceV2.terrainSnow : this.config.tileSource.terrainSnow) ??
                    (mapboxStandardStyleEnabled ? this.config.tileSourceV2.terrain : this.config.tileSource.terrain))
            : isSat
                ? mapboxStandardStyleEnabled
                    ? this.config.tileSourceV2.satellite
                    : this.config.tileSource.satellite
                : mapboxStandardStyleEnabled
                    ? this.config.tileSourceV2.terrain
                    : this.config.tileSource.terrain;
        return `mapbox://styles/polaris-riderx/${tileSource}?optimize=false`;
    }
    waitForStyleLoad(mapboxMap) {
        const mapLoaded$ = new BehaviorSubject(undefined);
        mapboxMap.once('load', (event) => {
            mapLoaded$.next(event.target);
        });
        return mapLoaded$.pipe(filter((isMapDefined) => Boolean(isMapDefined)), map((mapInstance) => mapInstance), switchMap((mapInstance) => fromEvent(mapInstance, 'style.load').pipe(switchMap((event) => timer(0, 60).pipe(filter(() => mapInstance.isStyleLoaded()), take(1), map(() => event.target))), startWith(mapInstance))));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: MapViewModelService, deps: [{ token: i1$1.ConfigService }, { token: MapboxService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: MapViewModelService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: MapViewModelService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1$1.ConfigService }, { type: MapboxService }] });

class MapComponent {
    constructor(mapViewModelService) {
        this.mapViewModelService = mapViewModelService;
        this.containerId = 'map';
        this.enableDragPan = false;
        this.mapInitialized = new EventEmitter();
        this.styleUpdated = new EventEmitter();
        this._isSat$ = new BehaviorSubject(undefined);
        this._isSno$ = new BehaviorSubject(false);
        this.destroyed = false;
    }
    set isSat(val) {
        this._isSat$.next(val);
    }
    set isSno(val) {
        this._isSno$.next(val);
    }
    ngOnInit() {
        defer(() => {
            this.mapViewModelService
                .create(this.containerId, this._isSat$, this._isSno$, this.enableDragPan)
                .pipe(takeWhile(() => !this.destroyed))
                .subscribe((vm) => {
                if (this._map === vm.map) {
                    this.styleUpdated.emit(vm.styleUpdated);
                }
                else {
                    this._map = vm.map;
                    this.mapInitialized.emit(vm.map);
                }
            });
        });
    }
    ngOnDestroy() {
        this.destroyed = true;
        this._map.remove();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: MapComponent, deps: [{ token: MapViewModelService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: MapComponent, isStandalone: true, selector: "pii-map", inputs: { containerId: "containerId", enableDragPan: "enableDragPan", isSat: "isSat", isSno: "isSno" }, outputs: { mapInitialized: "mapInitialized", styleUpdated: "styleUpdated" }, ngImport: i0, template: "<div class=\"map-container\" [id]=\"containerId\"></div>\n", styles: [".map-container{height:100%;position:relative}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: MapComponent, decorators: [{
            type: Component,
            args: [{ imports: [CommonModule], selector: 'pii-map', standalone: true, template: "<div class=\"map-container\" [id]=\"containerId\"></div>\n", styles: [".map-container{height:100%;position:relative}\n"] }]
        }], ctorParameters: () => [{ type: MapViewModelService }], propDecorators: { containerId: [{
                type: Input
            }], enableDragPan: [{
                type: Input
            }], mapInitialized: [{
                type: Output
            }], styleUpdated: [{
                type: Output
            }], isSat: [{
                type: Input
            }], isSno: [{
                type: Input
            }] } });

class MapService {
    constructor(logService, window) {
        this.logService = logService;
        this.window = window;
        this.className = 'MapService';
        this.maxSourceLoadAttempts = 1000; // give up after 1 minute
        this.sourceLoadedQueue = {};
        this.sourceLoadedObserver = {};
        this.sourceLoadAttempts = {};
    }
    getLayerStyle(mapboxMap, layerId) {
        const layer = mapboxMap.getLayer(layerId);
        if (!layer) {
            this.logService.warn(this.className, `Missing layer trying to get layer style: ${layerId}`);
            return undefined;
        }
        return layer;
    }
    getLayerIds(mapboxMap) {
        return mapboxMap.getStyle().layers.map((layer) => layer.id);
    }
    addDefaultSource(mapboxMap, sourceId) {
        if (mapboxMap.getSource(sourceId)) {
            mapboxMap.removeSource(sourceId);
        }
        mapboxMap.addSource(sourceId, this.createGeojsonSource());
        this.logService.debug(this.className, `added ${sourceId} source`);
    }
    replaceLayer(mapboxMap, layerId, sourceId, visible = true) {
        const newStyleLayer = this.copyMapLayer(mapboxMap, layerId, sourceId, visible);
        const layerIds = this.getLayerIds(mapboxMap);
        const layerIndex = layerIds.indexOf(layerId);
        let insertAfterLayerId;
        // append layer if existing layer is the top
        if (layerIndex >= layerIds.length - 1) {
            this.addLayer(mapboxMap, newStyleLayer);
        }
        else {
            if (layerIndex >= 0) {
                // layer IDs are in reverse order, so add one here instead of subtract one.
                insertAfterLayerId = layerIds[layerIndex + 1];
            }
            this.insertLayer(mapboxMap, newStyleLayer, insertAfterLayerId);
        }
    }
    setSourceData(mapboxMap, sourceId, data) {
        this.doWhenSourceLoaded(mapboxMap, sourceId, (source) => {
            this.logService.debug(this.className, `set source for ${sourceId}`);
            source.setData(data);
        });
    }
    // only tested on orv trails & labels and trail difficulty
    setLayerOpacity(mapboxMap, layerId, opacity) {
        if (!this.hasLayer(mapboxMap, layerId)) {
            this.logService.warn(this.className, `Tried to paint non-existent layer: ${layerId}`);
            return;
        }
        if (layerId.toLowerCase().includes('label')) {
            mapboxMap.setPaintProperty(layerId, 'text-opacity', opacity);
        }
        else if (layerId.toLowerCase() === 'difficulty') {
            mapboxMap.setPaintProperty(layerId, 'icon-opacity', opacity);
        }
        else {
            const currOpacity = mapboxMap.getPaintProperty(layerId, 'line-opacity');
            if (Array.isArray(currOpacity)) {
                currOpacity[currOpacity.length - 1] = opacity;
                mapboxMap.setPaintProperty(layerId, 'line-opacity', currOpacity);
            }
            else {
                mapboxMap.setPaintProperty(layerId, 'line-opacity', opacity);
            }
        }
    }
    hideLayer(mapboxMap, layerId) {
        if (!this.hasLayer(mapboxMap, layerId)) {
            this.logService.warn(this.className, `Tried to hide non-existent layer: ${layerId}`);
            return;
        }
        mapboxMap.setLayoutProperty(layerId, 'visibility', 'none');
    }
    showLayer(mapboxMap, layerId) {
        if (!this.hasLayer(mapboxMap, layerId)) {
            this.logService.warn(this.className, `Tried to show non-existent layer: ${layerId}`);
            return;
        }
        mapboxMap.setLayoutProperty(layerId, 'visibility', 'visible');
    }
    hasLayer(mapboxMap, layerName) {
        return !isEmpty(mapboxMap.getLayer(layerName));
    }
    addLayer(mapboxMap, layer) {
        if (mapboxMap.getLayer(layer.id)) {
            mapboxMap.removeLayer(layer.id);
        }
        mapboxMap.addLayer(layer);
    }
    /**
     * @description - Insert a layer in the map after an existing layer.
     * @param mapboxMap - An instance of Mapbox Map to insert the layer in.
     * @param layer - Layer to insert, including style definitions.
     * @param insertAfterLayerId - ID of the layer to insert after. Ideally, we would always be replacing an existing
     * layer in the Mapbox style, but we have to have a default here for when the style does not exist.
     * Obviously not ideal, as "waterway-label" can get moved or removed. All layer style definitions should get moved
     * to Mapbox Studio and we should call MapService.replaceLayer instead of this method.
     */
    insertLayer(mapboxMap, layer, insertAfterLayerId = 'waterway-label') {
        if (mapboxMap.getLayer(layer.id)) {
            mapboxMap.removeLayer(layer.id);
        }
        mapboxMap.addLayer(layer, insertAfterLayerId);
        this.logService.debug(this.className, {
            debugMessage: 'layer added',
            layer,
        });
    }
    copyMapLayer(mapboxMap, layerId, sourceId, visible = true) {
        const oldStyleLayer = this.getLayerStyle(mapboxMap, layerId) ?? {};
        delete oldStyleLayer['source-layer'];
        const newStyleLayer = { ...oldStyleLayer };
        newStyleLayer.source = sourceId;
        if (!newStyleLayer.layout) {
            newStyleLayer.layout = {};
        }
        // some LayerSpecifications don't have layout.visibility. previously we were always setting this, continue to do so
        newStyleLayer.layout.visibility = visible ? 'visible' : 'none';
        return newStyleLayer;
    }
    createGeojsonSource(geojson) {
        return {
            data: geojson || new GeoJsonFeatureCollection(),
            type: 'geojson',
        };
    }
    doWhenSourceLoaded(mapboxMap, sourceId, callback) {
        const source = mapboxMap.getSource(sourceId);
        if (source) {
            callback(source);
        }
        else {
            this.logService.debug(this.className, `waiting for ${sourceId} source to load`);
            if (this.sourceLoadedQueue[sourceId] === undefined) {
                this.sourceLoadedQueue[sourceId] = [];
            }
            this.sourceLoadedQueue[sourceId].push(callback);
            if (this.sourceLoadedObserver[sourceId] === undefined) {
                this.sourceLoadAttempts[sourceId] = 0;
                this.sourceLoadedObserver[sourceId] = this.window.setInterval(() => {
                    this.processSourceLoadedQueue(mapboxMap, sourceId);
                }, 60);
            }
        }
    }
    processSourceLoadedQueue(mapboxMap, sourceId) {
        let source;
        try {
            source = mapboxMap.getSource(sourceId);
        }
        catch (err) {
            this.logService.debug(this.className, {
                debugMessage: 'error getting source from map while processing source loaded queue',
                originalError: err,
                sourceId,
            });
            this.window.clearInterval(this.sourceLoadedObserver[sourceId]);
            return;
        }
        if (source) {
            this.logService.debug(this.className, `${sourceId} source loaded`);
            for (const queuedItem of this.sourceLoadedQueue[sourceId] || []) {
                queuedItem(source);
            }
            this.sourceLoadedQueue[sourceId] = [];
            this.window.clearInterval(this.sourceLoadedObserver[sourceId]);
            this.sourceLoadedObserver[sourceId] = undefined;
        }
        else {
            if (this.sourceLoadAttempts[sourceId] === undefined) {
                this.sourceLoadAttempts[sourceId] = 0;
            }
            this.sourceLoadAttempts[sourceId] += 1;
            if (this.sourceLoadAttempts[sourceId] >= this.maxSourceLoadAttempts) {
                this.logService.debug(this.className, `timed out waiting for ${sourceId} source to load`);
                this.window.clearInterval(this.sourceLoadedObserver[sourceId]);
            }
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: MapService, deps: [{ token: i1.LogService }, { token: WINDOW }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: MapService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: MapService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1.LogService }, { type: Window, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }] });

class MapAnimationService {
    constructor(consoleLogService) {
        this.consoleLogService = consoleLogService;
    }
    /**
     * Create a pulse dot animation object
     * @param mapboxMap Reference to a MapBox GL Map
     * @param options Options for the animation
     */
    createPulseDotAnimation(mapboxMap, options) {
        const color = this.convertColorString(options.color);
        const radiusStart = options.radiusStart || 15;
        const radiusEnd = options.radiusEnd || 35;
        const opacityStart = options.opacityStart || 1.0;
        const opacityEnd = options.opacityEnd || 0.03;
        const durationSeconds = options.duration || 2;
        const pixelRatio = 2;
        const padding = 5;
        const canvasSize = (radiusEnd * 2 + padding * 2) * pixelRatio;
        const pulseDot = {
            context: null,
            data: new Uint8ClampedArray(canvasSize * canvasSize * 4),
            easeMethod: this.easeInSine.bind(this),
            height: canvasSize,
            onAdd() {
                const canvas = document.createElement('canvas');
                canvas.width = this.width;
                canvas.height = this.height;
                this.context = canvas.getContext('2d');
            },
            pixelRatio,
            render() {
                const context = this.context;
                const duration = 1000 * durationSeconds;
                const time = (performance.now() % duration) / duration;
                const currentRadius = this.easeMethod(time, radiusStart * pixelRatio, radiusEnd * pixelRatio
                //duration
                );
                const currentOpacity = this.easeMethod(time, opacityStart, opacityEnd
                //duration
                );
                context.clearRect(0, 0, this.width, this.height);
                context.beginPath();
                context.arc(this.width / 2, this.height / 2, currentRadius, 0, Math.PI * 2);
                context.fillStyle = `rgba(${color.r}, ${color.g}, ${color.b}, ${currentOpacity})`;
                context.fill();
                this.data = context.getImageData(0, 0, this.width, this.height).data;
                mapboxMap.triggerRepaint();
                return true;
            },
            width: canvasSize,
        };
        return pulseDot;
    }
    /**
     * Convert `colorString` into `RGBAColor`
     * @param colorString
     */
    convertColorString(colorString) {
        let r = 0;
        let g = 0;
        let b = 0;
        const a = 0;
        if (colorString.includes('#')) {
            // 3 digits
            if (colorString.length === 4) {
                r = parseInt(colorString.charAt(1) + colorString.charAt(1), 16);
                g = parseInt(colorString.charAt(2) + colorString.charAt(2), 16);
                b = parseInt(colorString.charAt(3) + colorString.charAt(3), 16); // 6 digits
            }
            else if (colorString.length === 7) {
                r = parseInt(colorString.charAt(1) + colorString.charAt(2), 16);
                g = parseInt(colorString.charAt(3) + colorString.charAt(4), 16);
                b = parseInt(colorString.charAt(5) + colorString.charAt(6), 16);
            }
        }
        if (colorString.includes('rgb')) {
            // Implement Later
            this.consoleLogService.error('convertColor does not currently support rgb(#, #, #) values');
        }
        if (colorString.includes('rgba')) {
            // Implement Later
            this.consoleLogService.error('convertColor does not currently support rgba(#, #, #, #) values');
        }
        // Disable sort-keys to preserve this clever arrangement
        // eslint-disable-next-line sort-keys
        return { r, g, b, a };
    }
    /**
     * Ease In Sine
     * @param time
     * @param start
     * @param end
     */
    easeInSine(time, start, end) {
        const change = end - start;
        return -change * Math.cos(time * (Math.PI / 2)) + change + start;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: MapAnimationService, deps: [{ token: i1.ConsoleLogService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: MapAnimationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: MapAnimationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1.ConsoleLogService }] });

class MapContentService {
    constructor(consoleLogService, mapService) {
        this.consoleLogService = consoleLogService;
        this.mapService = mapService;
        this.defaultZoom = 10;
    }
    /**
     * set up sources and layers for map
     * @param map
     * @returns list of selected and unselected layers
     */
    initializeMap(map) {
        const layers = map.getStyle().layers.map((layer) => layer.id);
        //overwrite/add source and layers
        this.mapService.addDefaultSource(map, this.sourceId);
        const baseLayers = [
            ...this.filterLayers(layers, [this.layerPrefixActive, this.layerPrefixSelected]),
            ...this.getUnselectedLayerIds(layers),
        ];
        for (const layerId of baseLayers) {
            this.mapService.replaceLayer(map, layerId, this.sourceId);
        }
        //overwrite/add edit source and layers
        const editLayers = this.filterLayers(layers, [this.layerPrefixEditing]);
        if (editLayers.length) {
            this.mapService.addDefaultSource(map, this.editSourceId);
            for (const layerId of editLayers) {
                this.mapService.replaceLayer(map, layerId, this.editSourceId);
            }
        }
        return this.filterLayers(layers, [this.layerPrefixSelected, this.layerPrefixUnselected]);
    }
    setSourceData(map, geojson) {
        this.mapService.setSourceData(map, this.sourceId, geojson || new GeoJsonFeatureCollection());
    }
    filterLayers(layerIds, prefixes) {
        return layerIds.filter((id) => prefixes.some((pre) => id.toLowerCase().startsWith(pre.toLowerCase())));
    }
    getMinZoomFromStyle(map, layers) {
        let zoom = this.defaultZoom;
        for (const layerId of this.getUnselectedLayerIds(layers)) {
            const minZoom = this.mapService.getLayerStyle(map, layerId)?.minzoom;
            if (typeof minZoom === 'number') {
                zoom = minZoom;
            }
        }
        return zoom;
    }
    setupPrefixes(sourceId) {
        this.editSourceId = `${sourceId}Edit`;
        this.layerPrefixActive = `${sourceId}active`;
        this.layerPrefixEditing = `${sourceId}edit`;
        this.layerPrefixSelected = `${sourceId}selected`;
        this.layerPrefixUnselected = `${sourceId}unselected`;
    }
    getUnselectedLayerIds(layerIds) {
        return layerIds.filter((id) => id.toLowerCase().startsWith(this.layerPrefixUnselected.toLowerCase()) ||
            id.toLowerCase() === this.sourceId.toLowerCase());
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: MapContentService, deps: [{ token: i1.ConsoleLogService }, { token: MapService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: MapContentService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: MapContentService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1.ConsoleLogService }, { type: MapService }] });

class RideGroupMapContentService extends MapContentService {
    constructor(consoleLogService, mapService, mapAnimationService) {
        super(consoleLogService, mapService);
        this.mapAnimationService = mapAnimationService;
        this.minZoom = 10;
        this.sourceId = 'groupride';
        super.setupPrefixes(this.sourceId);
    }
    initializeMap(map) {
        const layers = super.initializeMap(map);
        this.setupAnimationLayers(map, layers);
        return layers;
    }
    setupAnimationLayers(mapboxMap, layers) {
        for (const layerId of layers.filter((layer) => layer.toLowerCase().includes('animation'))) {
            const currentLayer = mapboxMap.getLayer(layerId);
            const filter = currentLayer.filter ? [...currentLayer.filter] : undefined;
            const metadata = { ...currentLayer.metadata };
            const currentLayerIndex = layers.indexOf(layerId);
            const nextLayerId = layers[currentLayerIndex + 1];
            const color = mapboxMap.getPaintProperty(layerId, 'circle-color');
            const radius = mapboxMap.getPaintProperty(layerId, 'circle-radius');
            const pulseDotName = `${layerId}PulseDot`;
            const duration = layerId.toLowerCase().includes('activealert') ? 1 : 2;
            const pulseDotAnimation = this.mapAnimationService.createPulseDotAnimation(mapboxMap, {
                color,
                duration,
                radiusEnd: radius,
            });
            mapboxMap.addImage(pulseDotName, pulseDotAnimation, {
                pixelRatio: pulseDotAnimation.pixelRatio,
            });
            mapboxMap.removeLayer(layerId);
            mapboxMap.addLayer({
                filter,
                id: layerId,
                layout: {
                    'icon-allow-overlap': true,
                    'icon-image': pulseDotName,
                },
                metadata,
                source: this.sourceId,
                type: 'symbol',
            }, nextLayerId);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: RideGroupMapContentService, deps: [{ token: i1.ConsoleLogService }, { token: MapService }, { token: MapAnimationService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: RideGroupMapContentService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: RideGroupMapContentService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1.ConsoleLogService }, { type: MapService }, { type: MapAnimationService }] });

class TrailMapService {
    constructor(logService, mapService) {
        this.logService = logService;
        this.mapService = mapService;
        this.layerIdsOhm = [];
        this.layerIdsAtv = [];
        this.layerIdsRov50 = [];
        this.layerIdsRov = [];
        this.layerIdsAny = [];
        this.layerIdsDesignatedUnpaved = [];
        this.layerIdsClosed = [];
        this.layerIdsDifficulty = [];
        this.layerIdsDesignatedPaved = [];
        this.layerIdsUnmaintained = [];
        this.layerIdsOrv = [];
        this.layerIdsSno = [];
        this.layerIdsSelectable = [];
        this.sourceLayerIds = [];
        this.className = 'TrailMapService';
        this.opacity = [
            'case',
            ['in', '', ['get', 'vehicleTypes']],
            1,
            0.2,
        ];
    }
    addSources(mapboxMap) {
        const layerIds = this.mapService.getLayerIds(mapboxMap);
        this.layerIdsOhm = layerIds.filter((id) => {
            return (startsWith(id.toLowerCase(), 'trailsselectedohm') ||
                startsWith(id.toLowerCase(), 'trailsunselectedohm') ||
                startsWith(id.toLowerCase(), 'trailsohm'));
        });
        this.layerIdsAtv = layerIds.filter((id) => {
            return (startsWith(id.toLowerCase(), 'trailsselectedatv') ||
                startsWith(id.toLowerCase(), 'trailsunselectedatv') ||
                startsWith(id.toLowerCase(), 'trailsatv'));
        });
        this.layerIdsRov50 = layerIds.filter((id) => {
            return (startsWith(id.toLowerCase(), 'trailsselectedrov50') ||
                startsWith(id.toLowerCase(), 'trailsunselectedrov50') ||
                startsWith(id.toLowerCase(), 'trailsrov50'));
        });
        this.layerIdsRov = layerIds.filter((id) => {
            return (startsWith(id.toLowerCase(), 'trailsselectedrovl') ||
                startsWith(id.toLowerCase(), 'trailsunselectedrovl') ||
                startsWith(id.toLowerCase(), 'trailsrovl'));
        });
        this.layerIdsAny = layerIds.filter((id) => {
            return (startsWith(id.toLowerCase(), 'trailsselectedall') ||
                startsWith(id.toLowerCase(), 'trailsunselectedall') ||
                startsWith(id.toLowerCase(), 'trailsall'));
        });
        this.layerIdsDesignatedUnpaved = layerIds.filter((id) => {
            return (startsWith(id.toLowerCase(), 'trailsselectednon-designatedunpaved') ||
                startsWith(id.toLowerCase(), 'trailsunselectednon-designatedunpaved') ||
                startsWith(id.toLowerCase(), 'trailsnon-designatedunpaved'));
        });
        this.layerIdsDesignatedPaved = layerIds.filter((id) => {
            return (startsWith(id.toLowerCase(), 'trailsselectednon-designatedpaved') ||
                startsWith(id.toLowerCase(), 'trailsunselectednon-designatedpaved') ||
                startsWith(id.toLowerCase(), 'trailsnon-designatedpaved'));
        });
        this.layerIdsUnmaintained = layerIds.filter((id) => {
            return (startsWith(id.toLowerCase(), 'trailsselectedunmaintained') ||
                startsWith(id.toLowerCase(), 'trailsunselectedunmaintained') ||
                startsWith(id.toLowerCase(), 'trailsunmaintained'));
        });
        this.layerIdsClosed = layerIds.filter((id) => {
            return startsWith(id.toLowerCase(), 'trailsunselectedclosed') || startsWith(id.toLowerCase(), 'trailclosed');
        });
        this.layerIdsDifficulty = layerIds.filter((id) => {
            return startsWith(id.toLowerCase(), 'difficulty');
        });
        this.layerIdsOrv = [
            ...this.layerIdsOhm,
            ...this.layerIdsAtv,
            ...this.layerIdsRov50,
            ...this.layerIdsRov,
            ...this.layerIdsAny,
            ...this.layerIdsDesignatedPaved,
            ...this.layerIdsDesignatedUnpaved,
        ];
        this.layerIdsSno = layerIds.filter((id) => {
            return (startsWith(id.toLowerCase(), 'trailsselectedsno') ||
                startsWith(id.toLowerCase(), 'trailsunselectedsno') ||
                startsWith(id.toLowerCase(), 'trailssno'));
        });
        [...this.layerIdsOrv, ...this.layerIdsDifficulty].forEach((x) => {
            this.mapService.setLayerOpacity(mapboxMap, x, this.opacity);
        });
        this.layerIdsSelectable = [...this.layerIdsOrv, ...this.layerIdsSno, ...this.layerIdsUnmaintained];
        this.sourceLayerIds = [
            ...new Set(this.layerIdsSelectable
                .map((layer) => mapboxMap.getLayer(layer)?.['source-layer'] ?? '')
                .filter((x) => Boolean(x))),
        ];
        if (!this.sourceLayerIds.length) {
            this.logService.error(this.className, 'Unable to find trail data sourceId');
        }
    }
    mapIsDisplayingAnyTrailFeatures(mapboxMap, trailTypes, rideType) {
        const layers = rideType === 'SNO' ? this.layerIdsSno : this.layerIdsOrv;
        return (mapboxMap.queryRenderedFeatures({ layers }).filter((feature) => {
            const vehicleTypes = (feature.properties?.['vehicleTypes'] || []);
            return rideType === 'SNO' || trailTypes.some((type) => vehicleTypes.includes(type));
        }).length > 0);
    }
    setLayerOpacityMatch(mapboxMap, type) {
        this.opacity[1][1] = type ? `${type},` : type;
        [...this.layerIdsOrv, ...this.layerIdsDifficulty].forEach((layer) => {
            this.mapService.setLayerOpacity(mapboxMap, layer, this.opacity);
        });
    }
    setOhmLayerVisibility(mapboxMap, visible) {
        if (visible) {
            this.layerIdsOhm.forEach((x) => {
                this.mapService.setLayerOpacity(mapboxMap, x, 1);
            });
        }
        else {
            this.layerIdsOhm.forEach((x) => {
                this.mapService.setLayerOpacity(mapboxMap, x, 0.2);
            });
        }
    }
    setAtvLayerVisibility(mapboxMap, visible) {
        if (visible) {
            this.layerIdsAtv.forEach((x) => {
                this.mapService.setLayerOpacity(mapboxMap, x, 1);
            });
        }
        else {
            this.layerIdsAtv.forEach((x) => {
                this.mapService.setLayerOpacity(mapboxMap, x, 0.2);
            });
        }
    }
    setRov50LayerVisibility(mapboxMap, visible) {
        if (visible) {
            this.layerIdsRov50.forEach((x) => {
                this.mapService.setLayerOpacity(mapboxMap, x, 1);
            });
        }
        else {
            this.layerIdsRov50.forEach((x) => {
                this.mapService.setLayerOpacity(mapboxMap, x, 0.2);
            });
        }
    }
    setRovLayerVisibility(mapboxMap, visible) {
        if (visible) {
            this.layerIdsRov.forEach((x) => {
                this.mapService.setLayerOpacity(mapboxMap, x, 1);
            });
        }
        else {
            this.layerIdsRov.forEach((x) => {
                this.mapService.setLayerOpacity(mapboxMap, x, 0.2);
            });
        }
    }
    setAnyLayerVisibility(mapboxMap, visible) {
        if (visible) {
            this.layerIdsAny.forEach((x) => {
                this.mapService.setLayerOpacity(mapboxMap, x, 1);
            });
        }
        else {
            this.layerIdsAny.forEach((x) => {
                this.mapService.setLayerOpacity(mapboxMap, x, 0.2);
            });
        }
    }
    setDesignatedUnpavedLayerVisibility(mapboxMap, visible) {
        if (visible) {
            this.layerIdsDesignatedUnpaved.forEach((x) => {
                this.mapService.setLayerOpacity(mapboxMap, x, 1);
            });
        }
        else {
            this.layerIdsDesignatedUnpaved.forEach((x) => {
                this.mapService.setLayerOpacity(mapboxMap, x, 0.2);
            });
        }
    }
    setDesignatedPavedLayerVisibility(mapboxMap, visible) {
        if (visible) {
            this.layerIdsDesignatedPaved.forEach((x) => {
                this.mapService.setLayerOpacity(mapboxMap, x, 1);
            });
        }
        else {
            this.layerIdsDesignatedPaved.forEach((x) => {
                this.mapService.setLayerOpacity(mapboxMap, x, 0.2);
            });
        }
    }
    setUnmaintainedLayerVisibility(mapboxMap, visible) {
        if (visible) {
            this.layerIdsUnmaintained.forEach((x) => {
                this.mapService.setLayerOpacity(mapboxMap, x, 1);
            });
        }
        else {
            this.layerIdsUnmaintained.forEach((x) => {
                this.mapService.setLayerOpacity(mapboxMap, x, 0.2);
            });
        }
    }
    setClosedLayerVisibility(mapboxMap, visible) {
        if (visible) {
            this.layerIdsClosed.forEach((x) => {
                this.mapService.showLayer(mapboxMap, x);
            });
        }
        else {
            this.layerIdsClosed.forEach((x) => {
                this.mapService.hideLayer(mapboxMap, x);
            });
        }
    }
    setDifficultyLayerVisibility(mapboxMap, visible) {
        if (visible) {
            this.layerIdsDifficulty.forEach((x) => {
                this.mapService.showLayer(mapboxMap, x);
            });
        }
        else {
            this.layerIdsDifficulty.forEach((x) => {
                this.mapService.hideLayer(mapboxMap, x);
            });
        }
    }
    setOrvLayerVisibility(mapboxMap, visible) {
        if (visible) {
            this.layerIdsOrv.forEach((x) => {
                this.mapService.showLayer(mapboxMap, x);
            });
        }
        else {
            this.layerIdsOrv.forEach((x) => {
                this.mapService.hideLayer(mapboxMap, x);
            });
        }
    }
    setSnoLayerVisibility(mapboxMap, visible) {
        if (visible) {
            this.layerIdsSno.forEach((x) => {
                this.mapService.showLayer(mapboxMap, x);
            });
        }
        else {
            this.layerIdsSno.forEach((x) => {
                this.mapService.hideLayer(mapboxMap, x);
            });
        }
    }
    getTrailFeature(id, mapboxMap) {
        const features = this.sourceLayerIds.reduce((array, layerId) => {
            return [
                ...array,
                ...mapboxMap.querySourceFeatures('composite', {
                    sourceLayer: layerId,
                }),
            ];
        }, []);
        return features.find((x) => x.id === id || x.properties?.['id'] === id);
    }
    // find this filter expression within the outer 'all' filter specification
    // [
    //     'match',
    //     ['get', 'id'],
    //     ["SELECTED_ID"], // or a list of ids
    //     true, // or false
    //     false,// or true
    // ]
    // and update the ["SELECTED_ID"] array.
    setSelectedFilter(id, mapboxMap) {
        const newSelectedFilter = isArray(id) ? id : [id.toString() || 'SELECTED_ID'];
        for (const layerId of this.layerIdsSelectable) {
            const layerFilter = mapboxMap.getFilter(layerId);
            const idfilter = layerFilter?.find((exp) => isArray(exp[1]) && exp[1][1] === 'id');
            if (idfilter) {
                idfilter[2] = newSelectedFilter;
                mapboxMap.setFilter(layerId, layerFilter);
            }
            else {
                this.logService.debug(this.className, `failed to update selected filter for layer ${layerId}`);
            }
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: TrailMapService, deps: [{ token: i1.LogService }, { token: MapService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: TrailMapService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: TrailMapService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1.LogService }, { type: MapService }] });

class Adventure {
    constructor(contract = { type: 'Feature' }) {
        this.contract = cloneDeep(contract);
    }
    get adventuresBadgeImageUrl() {
        return this.properties['image-logoImage-0'] ?? '';
    }
    get addressLine1() {
        return this.properties.addressLine1 ?? '';
    }
    get city() {
        return this.properties.city ?? '';
    }
    get country() {
        return this.properties.country ?? '';
    }
    // dealerId may start with 8, this indicates it is from the DEX system, we'll treat those as not having a dealer
    get dealerId() {
        return this.properties.dealerNumber?.startsWith('8') ? '' : (this.properties.dealerNumber ?? '');
    }
    get galleryImages() {
        if (!this._galleryImages) {
            this._galleryImages = Object.keys(this.properties)
                .filter((key) => key.startsWith('image-gallery'))
                .map((key) => get(this, ['properties', key]));
        }
        return this._galleryImages;
    }
    get geoCoordinate() {
        return GeoCoordinate.fromCoordinateArray(this.geometry.coordinates);
    }
    get geometry() {
        if (!this.contract.geometry) {
            this.contract.geometry = {
                coordinates: [],
                type: 'Point',
            };
        }
        return this.contract.geometry;
    }
    get heroImageUrl() {
        return this.properties['image-landingHeroImage-0'] ?? '';
    }
    // in case the mapbox source is missing properties
    get isMissingProperties() {
        return !(this.id && this.name && this.programName && this.productLines && this.visibilityStatus);
    }
    get id() {
        return this.properties.id?.toString() ?? '';
    }
    get lifecycleStatus() {
        return this.properties.lifecycleStatus ?? '';
    }
    get name() {
        return this.properties.outfitterName ?? '';
    }
    get overview() {
        return this.properties.overview ?? '';
    }
    get polarisBadgeImageUrl() {
        return this.properties['image-logoImage-0'] ?? '';
    }
    get primaryPhone() {
        return this.properties.primaryPhone ?? '';
    }
    get productLines() {
        return this.properties.productLines ?? '';
    }
    get programName() {
        return this.properties.programName ?? '';
    }
    get properties() {
        if (!this.contract.properties) {
            this.contract.properties = {};
        }
        return this.contract.properties;
    }
    get stateProvince() {
        return this.properties.stateProvince ?? '';
    }
    get url() {
        return this.properties.url ?? '';
    }
    get visibilityStatus() {
        return this.properties.visibilityStatus ?? '';
    }
    get zip() {
        return this.properties.zip ?? '';
    }
    toGeoJson() {
        return {
            geometry: {
                coordinates: [...this.geometry.coordinates],
                type: 'Point',
            },
            properties: {
                ...cloneDeep(this.properties),
                name: this.name,
            },
            type: 'Feature',
        };
    }
}

class Dealer {
    constructor(contract = {}, defaultRideType = 'ORV') {
        this.contract = cloneDeep(contract);
        this.defaultRideType = defaultRideType;
        this._rideType = this.defaultRideType;
    }
    get address() {
        return new MailingAddress(this.properties.address);
    }
    get geoCoordinate() {
        return GeoCoordinate.fromCoordinateArray(this.geometry.coordinates);
    }
    get geometry() {
        if (!this.contract.geometry) {
            this.contract.geometry = {
                coordinates: [],
                type: 'Point',
            };
        }
        return this.contract.geometry;
    }
    get hours() {
        return DayHours.fromHoursArrayContract(this.properties.hours);
    }
    get id() {
        return this.properties.id ?? get(this, 'properties.dealerId', '');
    }
    get isInd() {
        if (this.productLines.length === 0) {
            return this.defaultRideType === 'IND';
        }
        return intersection(this.productLines, ['IND']).length > 0;
    }
    get isOrv() {
        if (this.productLines.length === 0) {
            return this.defaultRideType === 'ORV';
        }
        return intersection(this.productLines, ['ATV', 'RGR', 'RZR', 'GEN']).length > 0;
    }
    get isSlg() {
        if (this.productLines.length === 0) {
            return this.defaultRideType === 'SLG';
        }
        return intersection(this.productLines, ['SLG']).length > 0;
    }
    get isSno() {
        if (this.productLines.length === 0) {
            return this.defaultRideType === 'SNO';
        }
        return intersection(this.productLines, ['SNO']).length > 0;
    }
    get name() {
        return this.getNameByRideType();
    }
    get phone() {
        return this.properties.phone ?? this.properties.phoneNumber ?? '';
    }
    get productLines() {
        return [...(this.properties.productLines ?? [])];
    }
    get properties() {
        if (!this.contract.properties) {
            this.contract.properties = {};
        }
        return this.contract.properties;
    }
    set rideType(rideType) {
        this._rideType = rideType;
    }
    get website() {
        return this.properties.website || '';
    }
    static create(data, rideType) {
        const dealer = new Dealer(data, rideType);
        if (rideType) {
            dealer.rideType = rideType;
        }
        return dealer;
    }
    toDto() {
        return cloneDeep(this.contract);
    }
    toGeoJson() {
        return {
            geometry: {
                coordinates: [...this.geometry.coordinates],
                type: 'Point',
            },
            properties: {
                ...cloneDeep(this.properties),
                hasInd: this.isInd,
                hasOrv: this.isOrv,
                hasSno: this.isSno,
                name: this.getNameByRideType(),
            },
            type: 'Feature',
        };
    }
    clone() {
        const newDealer = new Dealer(this.contract, this.defaultRideType);
        newDealer.rideType = this._rideType;
        return newDealer;
    }
    getNameByRideType() {
        const rideType = this._rideType;
        const props = this.properties;
        if (props.indianName && (rideType === 'IND' || (this.isInd && !this.isOrv && !this.isSlg))) {
            return props.indianName;
        }
        else if (props.slingshotName && (rideType === 'SLG' || (this.isSlg && !this.isOrv && !this.isInd))) {
            return props.slingshotName;
        }
        return props.name ?? '';
    }
}

class Place {
    constructor(contract = { type: 'Feature' }) {
        this.contract = cloneDeep(contract);
    }
    get address() {
        return new MailingAddress(this.properties.address);
    }
    set address(address) {
        this.properties.address = address.toContract();
    }
    get email() {
        return this.properties.email || '';
    }
    set email(email) {
        this.properties.email = email;
    }
    get factualId() {
        return this.id;
    }
    get foursquareId() {
        return this.properties.foursquareId || '';
    }
    set foursquareId(foursquareId) {
        this.properties.foursquareId = foursquareId;
    }
    get geoCoordinate() {
        return GeoCoordinate.fromCoordinateArray(this.geometry.coordinates);
    }
    set geoCoordinate(geoCoordinate) {
        this.geometry.coordinates = geoCoordinate.toCoordinateArray();
    }
    get hours() {
        return new DayHours(this.properties.hours ?? {});
    }
    set hours(hours) {
        this.properties.hours = hours.toDictionaryContract();
    }
    get icon() {
        return this.getIconName();
    }
    get id() {
        return this.properties.id || '';
    }
    set id(id) {
        this.properties.id = id;
    }
    get name() {
        return this.properties.name || '';
    }
    set name(name) {
        this.properties.name = name;
    }
    get phone() {
        return this.properties.phone || '';
    }
    set phone(phone) {
        this.properties.phone = phone;
    }
    get placeType() {
        return this.properties.placeType ?? 'Other';
    }
    set placeType(placeType) {
        this.properties.placeType = placeType;
    }
    get properties() {
        if (!this.contract.properties) {
            this.contract.properties = {};
        }
        return this.contract.properties;
    }
    get type() {
        let placeTypeLowercase = this.placeType.toLowerCase();
        if (!placeTypeLowercase || placeTypeLowercase === 'unknown') {
            placeTypeLowercase = 'other';
        }
        return placeTypeLowercase;
    }
    get website() {
        return this.properties.website || '';
    }
    set website(website) {
        this.properties.website = website;
    }
    get geometry() {
        if (!this.contract.geometry) {
            this.contract.geometry = {
                coordinates: [],
                type: 'Point',
            };
        }
        return this.contract.geometry;
    }
    toContract() {
        return cloneDeep(this.contract);
    }
    // used by sponsor, planned ride addWaypointFromMap and pointOfInterestMapService
    toGeoJson() {
        return {
            geometry: {
                coordinates: [this.geoCoordinate.longitude, this.geoCoordinate.latitude],
                type: 'Point',
            },
            properties: {
                address: this.address,
                email: this.email,
                hours: this.hours.toDictionaryContract(),
                icon: this.icon,
                id: this.factualId,
                isSponsor: true,
                name: this.name,
                phone: this.phone,
                placeType: this.placeType,
                type: this.type,
                website: this.website,
            },
            type: 'Feature',
        };
    }
    getIconName() {
        return `poi-${this.type}`;
    }
}

class PremiumPlace extends Place {
    constructor(contract = { type: 'Feature' }) {
        super(contract);
        this.contract = cloneDeep(contract);
    }
    get description() {
        return this.properties.description || '';
    }
    set description(description) {
        this.properties.description = description;
    }
    get icon() {
        let icon = super.getIconName();
        if (this.isSponsorActive) {
            icon += '-premium';
        }
        return icon;
    }
    get isSponsor() {
        return this.properties.isSponsor || true;
    }
    set isSponsor(isSponsor) {
        this.properties.isSponsor = isSponsor;
    }
    get isSponsorActive() {
        return this.properties.isSponsorActive || false;
    }
    set isSponsorActive(isActive) {
        this.properties.isSponsorActive = isActive;
    }
    get modificationDate() {
        if (this.properties.modificationDate) {
            return new Date(this.properties.modificationDate);
        }
        return undefined;
    }
    get photos() {
        return this.properties.photos || [];
    }
    set photos(photos) {
        this.properties.photos = [...photos];
    }
    get sponsoredClubName() {
        return this.properties.sponsoredClubName || '';
    }
    set sponsoredClubName(clubName) {
        this.properties.sponsoredClubName = clubName;
    }
    get sponsoredClubWebsite() {
        return this.properties.sponsoredClubWebsite || '';
    }
    set sponsoredClubWebsite(website) {
        this.properties.sponsoredClubWebsite = website;
    }
    // TODO: CLI - The following properties are required until the
    // club-sponsor-list component gets split into two separate components
    get status() {
        return undefined;
    }
    get isSponsorExpired() {
        return undefined;
    }
    toContract() {
        const placeContract = super.toContract();
        return {
            geometry: placeContract.geometry,
            properties: {
                ...cloneDeep(this.contract.properties),
                ...cloneDeep(placeContract.properties),
            },
            type: placeContract.type,
        };
    }
    toGeoJson() {
        const geoJson = super.toGeoJson();
        geoJson.properties ??= {};
        geoJson.properties['description'] = this.description;
        geoJson.properties['isSponsor'] = this.isSponsor;
        geoJson.properties['isSponsorActive'] = this.isSponsorActive;
        geoJson.properties['modificationDate'] = this.modificationDate?.toISOString() ?? '';
        geoJson.properties['photos'] = cloneDeep(this.photos);
        return geoJson;
    }
    toPlace() {
        return new Place(this.toContract());
    }
}

class Sponsor extends PremiumPlace {
    constructor(placeContract = { type: 'Feature' }) {
        super(placeContract);
        this.contract = cloneDeep(placeContract);
    }
    get annualFees() {
        return this.properties.sponsorAnnualFees ?? undefined;
    }
    set annualFees(fees) {
        this.properties.sponsorAnnualFees = fees;
    }
    get annualFeesCurrency() {
        return this.properties.sponsorAnnualFeesCurrency || 'USD';
    }
    set annualFeesCurrency(currency) {
        this.properties.sponsorAnnualFeesCurrency = currency;
    }
    get expirationDate() {
        if (this.properties.expirationDate) {
            return new Date(this.properties.expirationDate);
        }
        return undefined;
    }
    set expirationDate(expirationDate) {
        if (!expirationDate) {
            this.properties.expirationDate = undefined;
            return;
        }
        this.properties.expirationDate = expirationDate.toISOString();
    }
    get expirationDateString() {
        if (!this.expirationDate) {
            return '';
        }
        return this.expirationDate.toISOString().substring(0, 10);
    }
    get isSponsorExpired() {
        return this.expirationDate ? this.expirationDate <= new Date() : false;
    }
    get notes() {
        return this.properties.clubManagerNotes || '';
    }
    set notes(notes) {
        this.properties.clubManagerNotes = notes;
    }
    get status() {
        if (this.isSponsorExpired) {
            return 'Expired';
        }
        else if (this.isSponsorActive) {
            return 'Active';
        }
        return 'Inactive';
    }
    static fromPlace(place) {
        return new Sponsor(place.toContract());
    }
    clone() {
        return new Sponsor(this.toContract());
    }
    cloneWithResponseContract(placeContract) {
        const combinedDto = merge({}, this.toContract(), placeContract);
        return new Sponsor(combinedDto);
    }
    toContract() {
        const placeContract = super.toContract();
        return {
            geometry: placeContract.geometry,
            properties: {
                ...cloneDeep(this.contract.properties),
                ...cloneDeep(placeContract.properties),
            },
            type: placeContract.type,
        };
    }
    toPremiumPlace() {
        return new PremiumPlace(super.toContract());
    }
    toGeoJson() {
        const geoJson = this.toContract();
        // add any display properties
        return geoJson;
    }
}

class RideArea {
    constructor(contract = { type: 'Feature' }, defaultVehicleClass = 'ORV') {
        this.isInd = false;
        this.isSlg = false;
        this.contract = cloneDeep(contract);
        this._defaultVehicleClass = defaultVehicleClass;
    }
    get areaSize() {
        return this.properties.areaSize;
    }
    get callToAction() {
        return this.properties.callToAction ?? [];
    }
    get contacts() {
        return this.properties.contacts || [];
    }
    get coordinates() {
        return this.geometry.coordinates || [];
    }
    get description() {
        return this.properties.description || '';
    }
    get difficulty() {
        return this.properties.difficulty || {};
    }
    get geoCoordinate() {
        return GeoCoordinate.fromCoordinateArray(this.coordinates);
    }
    get geometry() {
        if (!this.contract.geometry) {
            this.contract.geometry = {
                coordinates: [],
                type: 'Point',
            };
        }
        return this.contract.geometry;
    }
    get hasAdmissionFee() {
        return this.properties.admissionFee === 'Y';
    }
    get id() {
        return this.contract.id || '';
    }
    get isOrv() {
        return intersection(this.vehicleClasses, ['ATV', 'ROV', 'ROV50', 'ORV', 'OHV', 'UTV', 'OHM', 'HLV']).length > 0;
    }
    get isSno() {
        return intersection(this.vehicleClasses, ['SNO']).length > 0;
    }
    get isOpen() {
        return this.getIsInSeason();
    }
    get logo() {
        return this.properties.logo || '';
    }
    get mainAddress() {
        return new MailingAddress(this.properties.mainAddress);
    }
    get name() {
        return this.properties.name || '';
    }
    get parsedTrailSystemType() {
        return RideArea.getParsedTrailSystemType(this.trailSystemType);
    }
    get properties() {
        if (!this.contract.properties) {
            this.contract.properties = { name: '', uuid: '' };
        }
        return this.contract.properties;
    }
    get season() {
        if (!this._season) {
            this._season = this.properties.season;
            if (!this._season) {
                this._season = { begin: '', end: '' };
            }
        }
        return this._season;
    }
    get totalMiles() {
        return this.properties.totalMiles;
    }
    get trailSystemType() {
        return this.properties.trailSystemType || 'TrailSystem';
    }
    get vehicleClasses() {
        return this.properties.vehicleClasses || [this._defaultVehicleClass];
    }
    static getParsedTrailSystemType(type) {
        switch (type) {
            case 'TrailSystem':
                return 'Trail System';
            case 'PrivateRidePark':
                return 'Private Ride Park';
            default:
                return '';
        }
    }
    toDto() {
        return cloneDeep(this.contract);
    }
    toGeoJson() {
        return {
            ...this.contract,
            properties: {
                ...this.properties,
                id: this.contract.id,
            },
        };
    }
    getIsInSeason() {
        let seasonStart;
        let seasonEnd;
        if (this.season.begin) {
            seasonStart = new Date(this.season.begin);
        }
        if (this.season.end) {
            seasonEnd = new Date(this.season.end);
        }
        if (!seasonStart || !seasonEnd) {
            return true;
        }
        const today = new Date();
        const thisYear = today.getFullYear();
        // convert season dates to local dates in the current year
        const start = new Date(thisYear, seasonStart.getUTCMonth(), seasonStart.getUTCDate());
        const end = new Date(thisYear, seasonEnd.getUTCMonth(), seasonEnd.getUTCDate());
        if (start > end) {
            return today >= start || today <= end;
        }
        return today >= start && today <= end;
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { Adventure, ConditionCreateEditComponent, Dealer, MapComponent, MapService, MapViewModelService, MapboxService, Place, PremiumPlace, PrimaryMapService, RideArea, RideGroupMapContentService, Sponsor, TrailMapService };
